import { useEffect, useMemo, useRef, useState } from 'react'
import { Keyboard } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useAlertsInfinite } from './hooks/useAlertsInfinite'
import { useBulkSelection } from './hooks/useBulkSelection'
import { useQueueKeyboard } from './hooks/useQueueKeyboard'
import { useAlertsWebSocket } from './hooks/useAlertsWebSocket'
import { useSavedFilters } from './hooks/useSavedFilters'
import { AlertsTable } from './components/queue/AlertsTable'
import { FilterBar } from './components/queue/FilterBar'
import { SavedFilterTabs } from './components/queue/SavedFilterTabs'
import { BulkActionsBar } from './components/queue/BulkActionsBar'
import { LiveIndicator } from './components/queue/LiveIndicator'
import { NewAlertsToast } from './components/queue/NewAlertsToast'
import { ShortcutsModal } from './components/queue/ShortcutsModal'
import { ExportCsvButton } from './components/queue/ExportCsvButton'
import { SaveFilterDialog } from './components/queue/SaveFilterDialog'
import type { AlertFilters, SavedFilter, SortKey } from './types'

/**
 * AF-126 — alerts queue page.
 *
 * Composes saved-filter tabs + filter chip bar + virtualized table + bulk
 * actions bar + real-time indicator. Keyboard shortcuts (j/k/Enter/space/x/?)
 * scoped to this page via `useQueueKeyboard`.
 */
export function AlertsQueuePage() {
  const [filters, setFilters] = useState<AlertFilters>({ status: ['open', 'in_review'] })
  const [sort, setSort] = useState<SortKey[]>([{ field: 'created_at', dir: 'desc' }])
  const [activeSavedId, setActiveSavedId] = useState<string | null>(null)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [userIsAtTop, setUserIsAtTop] = useState(true)

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const queryArgs = useMemo(() => ({ filters, sort }), [filters, sort])
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAlertsInfinite(queryArgs)
  const alerts = useMemo(() => data?.pages.flatMap((p) => p.alerts) ?? [], [data?.pages])
  const total = data?.pages[0]?.total ?? 0

  const selection = useBulkSelection<string>()

  const ws = useAlertsWebSocket({ userIsAtTop })

  // Track scrollTop to decide when to buffer vs. fade-in new alerts.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => setUserIsAtTop(el.scrollTop <= 100)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  // Document title pings while tab is hidden and new alerts are buffered.
  useEffect(() => {
    if (ws.bufferedNewCount > 0 && document.visibilityState === 'hidden') {
      document.title = `(${ws.bufferedNewCount}) Antifraud — alerts`
    } else {
      document.title = 'Antifraud — alerts'
    }
    return () => {
      document.title = 'Antifraud'
    }
  }, [ws.bufferedNewCount])

  const handleSort = (field: SortKey['field'], multi: boolean) => {
    setSort((prev) => {
      const existing = prev.find((s) => s.field === field)
      if (!multi) {
        if (existing) return [{ field, dir: existing.dir === 'asc' ? 'desc' : 'asc' }]
        return [{ field, dir: 'desc' }]
      }
      // Multi-sort with shift-click — append, or flip if already in the list.
      if (existing) {
        return prev.map((s) =>
          s.field === field ? { ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' } : s,
        )
      }
      return [...prev, { field, dir: 'desc' }]
    })
  }

  const orderedIds = useMemo(() => alerts.map((a) => a.id), [alerts])

  const onToggleSelect = (id: string, range: boolean) => {
    selection.toggle(id, range, orderedIds)
  }

  const onToggleSelectAll = () => {
    if (selection.count === alerts.length) selection.clear()
    else selection.setAll(orderedIds)
  }

  const { focusedIndex } = useQueueKeyboard({
    alertIds: orderedIds,
    onToggleSelect,
    onToggleSelectAll,
    onClearSelection: selection.clear,
    onShowShortcuts: () => setShortcutsOpen(true),
    onFocusSearch: () => searchInputRef.current?.focus(),
  })

  const handleSelectSavedFilter = (sf: SavedFilter | null) => {
    setActiveSavedId(sf?.id ?? null)
    setFilters(sf?.filters ?? {})
  }

  const handleClearFilters = () => {
    setFilters({})
    setActiveSavedId(null)
  }

  const handleResetToMyQueue = () => {
    const my = savedQuery.data?.find((f) => f.id === 'sf-mine')
    if (my) {
      setActiveSavedId('sf-mine')
      setFilters(my.filters)
    }
  }

  const [saveFilterOpen, setSaveFilterOpen] = useState(false)
  const savedQuery = useSavedFilters()

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-xl font-semibold">Alerts</h1>
          <p className="text-xs text-muted-fg">
            {total} total · {alerts.length} loaded
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LiveIndicator
            paused={ws.paused}
            bufferedCount={ws.bufferedNewCount}
            onTogglePause={() => ws.setPaused(!ws.paused)}
          />
          <ExportCsvButton filters={filters} sort={sort} />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Keyboard shortcuts"
            onClick={() => setShortcutsOpen(true)}
          >
            <Keyboard size={16} />
          </Button>
        </div>
      </header>

      <SavedFilterTabs
        activeId={activeSavedId}
        onSelect={handleSelectSavedFilter}
        onCreate={() => setSaveFilterOpen(true)}
      />

      <FilterBar
        filters={filters}
        onChange={(next) => {
          setFilters(next)
          setActiveSavedId(null)
        }}
        onClearAll={handleClearFilters}
      />

      <div className="relative flex-1 min-h-0">
        <NewAlertsToast
          count={ws.bufferedNewCount}
          onClick={() => {
            ws.acknowledgeBuffered()
            scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        />
        <AlertsTable
          alerts={alerts}
          loading={isLoading}
          error={error}
          filters={filters}
          sort={sort}
          selectedIds={selection.selected}
          focusedIndex={focusedIndex}
          recentlyChangedIds={ws.recentlyChangedIds}
          onSort={handleSort}
          onToggleSelect={onToggleSelect}
          onToggleSelectAll={onToggleSelectAll}
          onClearFilters={handleClearFilters}
          onResetToMyQueue={handleResetToMyQueue}
          onRetry={() => void refetch()}
          onLoadMore={() => void fetchNextPage()}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          scrollContainerRef={scrollRef}
        />
      </div>

      <BulkActionsBar
        selectedIds={Array.from(selection.selected)}
        alerts={alerts}
        onClear={selection.clear}
      />

      <ShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <SaveFilterDialog
        open={saveFilterOpen}
        onOpenChange={setSaveFilterOpen}
        filters={filters}
        onSaved={(saved) => {
          setActiveSavedId(saved.id)
        }}
      />
    </div>
  )
}
